import React from "react";
import { Award, Sparkles, GraduationCap, Trophy, Building2, Globe2, ArrowUpRight } from "lucide-react";
import { STRENGTHS } from "../lib/content";
import JourneyMapCard from "./JourneyMapCard";

const ICONS = { Award, Sparkles, GraduationCap, Trophy, Building2, Globe2 };

// Per-card visual treatment: dark cards punctuate the grid, plus a proof-stat for each.
const VARIANTS = [
  { theme: "dark",  metric: "3",     unit: "Registered Patents" },
  { theme: "light", metric: "8",     unit: "AI Platforms Live" },
  { theme: "light", metric: "PhD",   unit: "Eastern Finland" },
  { theme: "dark",  metric: "1st & 2nd", unit: "Intel ISEF — World" },
  { theme: "light", metric: "14+",   unit: "Years Leading" },
  { theme: "light", metric: "12",    unit: "Countries Crossed" },
];

const Strengths = () => (
  <section
    id="strengths"
    data-testid="section-strengths"
    className="relative py-16 sm:py-20 bg-alabaster-surface overflow-hidden"
  >
    <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" aria-hidden />

    <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
      <div className="text-center max-w-3xl mx-auto reveal">
        <div className="eyebrow inline-flex items-center gap-3">
          <span className="block w-10 h-px bg-terracotta" />
          Why Work With Me
          <span className="block w-10 h-px bg-terracotta" />
        </div>
        <h2
          data-testid="strengths-heading"
          className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-ink leading-[1.08] tracking-tight"
        >
          Not just an <span className="text-terracotta">academic</span>. <br />
          Not just a <span className="text-terracotta">builder</span>.
        </h2>
      </div>

      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-5 auto-rows-fr">
        {STRENGTHS.map((s, i) => {
          const Icon = ICONS[s.icon] || Award;
          const v = VARIANTS[i] || VARIANTS[0];
          const dark = v.theme === "dark";

          return (
            <div
              key={s.title}
              data-testid={`strength-card-${i}`}
              className={`reveal group relative rounded-3xl p-6 sm:p-7 overflow-hidden transition-all duration-500 hover:-translate-y-2 ${
                dark
                  ? "bg-ink text-alabaster hover:shadow-[0_30px_60px_-25px_rgba(245,187,31,0.45)]"
                  : "bg-white border border-alabaster-line text-ink hover:shadow-[0_30px_60px_-30px_rgba(28,32,30,0.25)] hover:border-terracotta/40"
              }`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              {/* Decorative corner glow */}
              <div
                className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-40 pointer-events-none transition-opacity group-hover:opacity-70"
                style={{
                  background: dark
                    ? "radial-gradient(circle, rgba(245,187,31,0.35), transparent 60%)"
                    : "radial-gradient(circle, rgba(245,187,31,0.18), transparent 60%)",
                }}
                aria-hidden
              />

              {/* Header row: icon + ordinal */}
              <div className="flex items-center justify-between">
                <span
                  className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl transition-colors ${
                    dark
                      ? "bg-terracotta text-ink"
                      : "bg-terracotta/15 text-terracotta group-hover:bg-terracotta group-hover:text-alabaster"
                  }`}
                >
                  <Icon size={22} strokeWidth={1.8} />
                </span>
                <span
                  className={`font-display text-[36px] sm:text-[40px] font-bold leading-none tracking-tight ${
                    dark ? "text-alabaster/20" : "text-alabaster-line"
                  } group-hover:text-terracotta/60 transition-colors`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>

              {/* Title */}
              <h3
                className={`font-display mt-6 text-xl font-bold leading-snug ${
                  dark ? "text-alabaster" : "text-ink"
                }`}
              >
                {s.title}
              </h3>

              {/* Proof metric */}
              <div
                className={`mt-4 inline-flex items-baseline gap-2 ${
                  dark ? "" : ""
                }`}
              >
                <span
                  className={`font-display text-3xl font-bold tracking-tight leading-none ${
                    dark ? "text-terracotta" : "text-terracotta"
                  }`}
                >
                  {v.metric}
                </span>
                <span
                  className={`text-[11px] font-display font-bold uppercase tracking-widest ${
                    dark ? "text-alabaster/70" : "text-ink-soft"
                  }`}
                >
                  {v.unit}
                </span>
              </div>

              {/* Description */}
              <p
                className={`mt-5 text-sm leading-relaxed ${
                  dark ? "text-alabaster/80" : "text-ink-soft"
                }`}
              >
                {s.desc}
              </p>

              {/* Tags */}
              <div className="mt-6 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span
                    key={t}
                    className={`text-[10px] font-semibold tracking-widest uppercase rounded-full px-3 py-1 ${
                      dark
                        ? "bg-alabaster/10 text-alabaster/85 border border-alabaster/15"
                        : "bg-terracotta/10 text-terracotta border border-terracotta/20"
                    }`}
                  >
                    {t}
                  </span>
                ))}
              </div>

              {/* Hover arrow */}
              <div
                className={`absolute bottom-6 right-6 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 ${
                  dark
                    ? "bg-terracotta text-ink"
                    : "bg-ink text-alabaster"
                }`}
                aria-hidden
              >
                <ArrowUpRight size={16} />
              </div>
            </div>
          );
        })}
        {/* Compact world-map card filling the remaining 2 slots in row 2 */}
        <JourneyMapCard />
      </div>
    </div>
  </section>
);

export default Strengths;
